import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, User } from '../services/authService';
import { ApiService } from '../services/api';

export interface CurrentUserInfo {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  status?: string | null;
  userId?: string | null;
  invitedAt?: string | null;
  joinedAt?: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  consultant: any | null;
  currentUser: CurrentUserInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshConsultant: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [consultant, setConsultant] = useState<any | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshConsultant = async () => {
    const token = authService.getAccessToken();
    if (!token) {
      setConsultant(null);
      setCurrentUser(null);
      return;
    }
    try {
      const resp = await ApiService.getConsultantMe(token);
      setConsultant(resp?.data ?? null);
      setCurrentUser((resp as any)?.data?.currentUser ?? null);
    } catch (e) {
      console.error('Failed to fetch consultant profile:', e);
      setConsultant(null);
      setCurrentUser(null);

      // If /consultants/me fails, treat session as invalid (do not allow login)
      try {
        sessionStorage.setItem('loginGateReason', 'profile_error');
        sessionStorage.setItem(
          'loginGateMessage',
          'We could not load your consultant profile. Please try again or use a different account.',
        );
        await authService.clearLocalUser();
      } catch {}
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check if user is already authenticated
        const isAuth = await authService.checkAuthStatus();
        const currentUser = authService.getUser();
        
        setIsAuthenticated(isAuth);
        setUser(currentUser);
        if (isAuth) {
          await refreshConsultant();
        } else {
          setConsultant(null);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setIsAuthenticated(false);
        setUser(null);
        setConsultant(null);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange((isAuth, authUser) => {
      setIsAuthenticated(isAuth);
      setUser(authUser);
      if (!isAuth) {
        setConsultant(null);
        setCurrentUser(null);
        setIsLoading(false);
        return;
      }

      // When a new user is loaded, fetch consultant profile in background
      refreshConsultant().finally(() => {
        setIsLoading(false);
      });
    });

    return unsubscribe;
  }, []);

  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.login();
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setConsultant(null);
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    consultant,
    currentUser,
    login,
    logout,
    refreshConsultant,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
